// src/utils/errorContext.js
//
// NZSC-4015: 오류 페이지 원인 문맥 버퍼 — web-core `src/lib/errorContext.ts` 와 **같은 계약**
// (sessionStorage 키·형식). /analyze/error 는 web-core 가 서빙하므로 Vue 가 여기 남긴 기록을
// 풀 로드 뒤 web-core 오류 페이지가 읽어 참조번호와 함께 보고한다. 한쪽만 바꾸면 크로스앱
// 발신의 문맥이 사라진다.
//
// 관문 4곳이 기록한다: api.js 인터셉터(마지막 API 실패) · pgResultProbe(PG 콜백) ·
// main.js 전역 예외 핸들러(JS 예외) · router beforeEach(직전 화면·원인 코드).
// 개인정보·토큰·요청/응답 본문은 넣지 않는다. 이 모듈은 절대 예외를 밖으로 흘리지 않는다.

export const ERROR_CONTEXT_KEYS = {
  api: "nz.err.api",
  pg: "nz.err.pg",
  js: "nz.err.js",
  from: "nz.err.from",
};

const LIMIT = { path: 200, message: 200, jsMessage: 300, source: 200, from: 500, pgCode: 50 };

/** 기록에 실으면 안 되는 쿼리 키(토큰·세션·비밀). 값은 [redacted] 로 — 키가 있었다는 사실은 남긴다 (web-core 와 동일) */
const SENSITIVE_QUERY_KEY = /token|session|secret|password|passwd|auth|key$/i;
export function normalizeAppPath(pathWithSearch) {
  const s = String(pathWithSearch || "");
  const path = s.replace(/^\/next(?=\/|\?|$)/, "") || "/";
  const qi = path.indexOf("?");
  if (qi < 0) return path;
  const params = new URLSearchParams(path.slice(qi + 1));
  let touched = false;
  for (const k of Array.from(params.keys())) {
    if (SENSITIVE_QUERY_KEY.test(k)) {
      params.set(k, "[redacted]");
      touched = true;
    }
  }
  return touched ? `${path.slice(0, qi)}?${params.toString()}` : path;
}

const clip = (v, n) => {
  if (v === undefined || v === null) return undefined;
  const s = String(v);
  return s.length > n ? s.slice(0, n) : s;
};

const storage = () => {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch (e) {
    return null;
  }
};

function write(key, value) {
  try {
    const st = storage();
    if (st) st.setItem(key, JSON.stringify(value));
  } catch (e) {
    /* 저장 실패가 화면을 막아선 안 된다 */
  }
}
function read(key) {
  try {
    const st = storage();
    const raw = st ? st.getItem(key) : null;
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

/** API 관문 — HTTP 실패(!2xx)·응답 없음(status 0)·업무 실패(code!==0) */
export function recordApiFailure({ path, status, code, message }) {
  write(ERROR_CONTEXT_KEYS.api, {
    path: clip(String(path || "").split("?")[0], LIMIT.path) || "",
    status: Number.isFinite(Number(status)) ? Number(status) : 0,
    code: typeof code === "number" ? code : undefined,
    message: clip(message, LIMIT.message),
    at: Date.now(),
  });
}

/** PG 관문 — 결제창 취소·실패 결과 */
export function recordPgResult({ provider, code, message }) {
  write(ERROR_CONTEXT_KEYS.pg, {
    provider,
    code: clip(code, LIMIT.pgCode) || "",
    message: clip(message, LIMIT.message),
    at: Date.now(),
  });
}

/** 전역 예외 관문 — 미처리 예외·거부 프로미스 */
export function recordJsError({ message, source }) {
  write(ERROR_CONTEXT_KEYS.js, {
    message: clip(message, LIMIT.jsMessage) || "",
    source: clip(source, LIMIT.source),
    at: Date.now(),
  });
}

/** 발신부가 미리 남긴 from 기록(원인 코드·금액 등)을 라우터 관문이 덮지 않도록 들여다본다 */
export function peekFrom(maxAgeMs = 2000) {
  const r = read(ERROR_CONTEXT_KEYS.from);
  return r && Date.now() - r.at <= maxAgeMs ? r : null;
}

/** NZSC-4245: 마지막 API 실패를 지우지 않고 들여다본다(라우터 관문의 로그인 끊김 판단 — 401 인지) */
export function peekApiFailure() {
  return read(ERROR_CONTEXT_KEYS.api);
}

/** 라우터 관문·발신부 — 오류 페이지로 가기 직전의 화면과(있으면) 원인 코드·금액 */
export function recordFrom({ from, sender, code, price, addressStatus, contractType }) {
  const p = Number(price);
  write(ERROR_CONTEXT_KEYS.from, {
    from: clip(normalizeAppPath(from), LIMIT.from) || "",
    sender: sender || "vue",
    code: code || undefined,
    price: Number.isFinite(p) ? Math.max(0, Math.trunc(p)) : undefined,
    addressStatus: clip(addressStatus, 50),
    contractType: clip(contractType, 50),
    at: Date.now(),
  });
}

/** 발신 직전 기록으로 인정하는 최대 경과(ms). 그보다 오래된 실패는 이번 오류와 무관하다고 본다 */
export const ERROR_CONTEXT_MAX_AGE_MS = 10000;
/** 발신(from.at) 뒤 이만큼 안에 완료된 기록도 인정 — 비동기 본문 판독 뒤 갱신되는 API 기록 대비(web-core 와 동일) */
export const ERROR_CONTEXT_AFTER_FROM_MS = 2000;

function remove(key) {
  try {
    const st = storage();
    if (st) st.removeItem(key);
  } catch (e) {
    /* noop */
  }
}

/**
 * 오류 페이지가 한 번 읽고 지운다(같은 기록이 다음 오류에 다시 붙지 않게).
 * api/pg/js 는 기준 시각(from.at, 없으면 지금) 기준 ERROR_CONTEXT_MAX_AGE_MS 안의 것만 인정한다.
 */
export function consumeErrorContext(now = Date.now()) {
  const from = read(ERROR_CONTEXT_KEYS.from);
  const base = (from && from.at) || now;
  const fresh = r =>
    r && base - r.at <= ERROR_CONTEXT_MAX_AGE_MS && r.at - base <= ERROR_CONTEXT_AFTER_FROM_MS ? r : null;
  const snap = {
    from,
    api: fresh(read(ERROR_CONTEXT_KEYS.api)),
    pg: fresh(read(ERROR_CONTEXT_KEYS.pg)),
    js: fresh(read(ERROR_CONTEXT_KEYS.js)),
  };
  Object.values(ERROR_CONTEXT_KEYS).forEach(remove);
  return snap;
}

/**
 * 참조번호 — NZ- + 6자리, 헷갈리는 글자(0/O/1/I) 뺀 32자 집합, crypto 난수. 아무 정보도 담지 않는다.
 * 서버 DTO(FUNNEL_ERROR_REF_PATTERN)·web-core makeErrorRef 와 같은 형식.
 */
const REF_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
export function makeErrorRef() {
  const bytes = new Uint8Array(6);
  try {
    window.crypto.getRandomValues(bytes);
  } catch (e) {
    for (let i = 0; i < 6; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  let out = "";
  for (let i = 0; i < 6; i++) out += REF_ALPHABET[bytes[i] % 32];
  return `NZ-${out}`;
}
